'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  PieChart,
  Globe,
  Shield,
  Zap,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Star,
  Check,
  Menu,
  X,
} from 'lucide-react';

/* ─────────────────────── DATA ─────────────────────── */

const stats = [
  { value: '10+', label: 'Years of Experience' },
  { value: '25K+', label: 'Satisfied Customers' },
  { value: '180+', label: 'Trading Instruments' },
  { value: '<12ms', label: 'Execution Speed' },
];

const features = [
  {
    icon: 'TrendingUp',
    title: 'High Profit Potential',
    description:
      'Leverage market movements across multiple asset classes to maximize your portfolio returns with professional-grade tools.',
  },
  {
    icon: 'Users',
    title: 'Accessibility for All',
    description:
      "Whether you're a beginner or a seasoned professional, our platform adapts to your experience level with intuitive tools.",
  },
  {
    icon: 'PieChart',
    title: 'Portfolio Diversification',
    description:
      'Spread your investments across Forex, Stocks, Indices, Commodities and Crypto — all from a single account.',
  },
  {
    icon: 'Globe',
    title: 'Global Liquidity',
    description:
      'Access deep institutional liquidity pools ensuring tight spreads and fast execution across all market conditions.',
  },
  {
    icon: 'Shield',
    title: 'Bank-Grade Security',
    description:
      'Your funds are held in segregated accounts at top-tier banks with negative balance protection and full encryption.',
  },
  {
    icon: 'Zap',
    title: 'Lightning Execution',
    description:
      'Sub-12ms average execution speed with zero requotes. Our infrastructure is co-located with major liquidity providers.',
  },
];

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  TrendingUp,
  Users,
  PieChart,
  Globe,
  Shield,
  Zap,
};

const markets = [
  {
    name: 'Forex',
    pairs: '60+ pairs',
    spreads: 'From 0.0 pips',
    leverage: 'Up to 1:500',
    description: 'Major, minor and exotic currency pairs with deep liquidity',
    examples: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/CAD'],
  },
  {
    name: 'Stocks',
    pairs: '500+ CFDs',
    spreads: 'From 0.1%',
    leverage: 'Up to 1:20',
    description: 'Trade global equities from NYSE, NASDAQ, LSE, and more',
    examples: ['Apple', 'Tesla', 'Amazon', 'NVIDIA'],
  },
  {
    name: 'Indices',
    pairs: '20+ indices',
    spreads: 'From 0.4 pts',
    leverage: 'Up to 1:200',
    description: "Access the world's major stock indices in a single click",
    examples: ['S&P 500', 'DAX 40', 'FTSE 100', 'Nikkei 225'],
  },
  {
    name: 'Commodities',
    pairs: '15+ instruments',
    spreads: 'From 0.03',
    leverage: 'Up to 1:100',
    description: 'Precious metals, energy and soft commodities',
    examples: ['Gold', 'Silver', 'Crude Oil', 'Natural Gas'],
  },
  {
    name: 'Crypto',
    pairs: '30+ pairs',
    spreads: 'From 0.1%',
    leverage: 'Up to 1:10',
    description: 'Trade the top cryptocurrencies 24/7 with competitive spreads',
    examples: ['BTC/USD', 'ETH/USD', 'SOL/USD', 'XRP/USD'],
  },
];

const accounts = [
  {
    name: 'Standard',
    minDeposit: '$10,000',
    popular: false,
    features: [
      '180+ trading instruments',
      'Personal account manager',
      'Daily market analysis',
      'Email & chat support',
      'Demo account included',
      'Mobile trading app',
    ],
  },
  {
    name: 'Bronze',
    minDeposit: '$25,000',
    popular: false,
    features: [
      'Everything in Standard',
      'Priority support 24/5',
      'Weekly trading webinars',
      'Advanced charting suite',
      'Risk management tools',
      'Trading signals',
    ],
  },
  {
    name: 'Silver',
    minDeposit: '$50,000',
    popular: false,
    features: [
      'Everything in Bronze',
      'Dedicated senior analyst',
      'Custom trading strategies',
      'Reduced spreads',
      'Monthly portfolio review',
      'Economic calendar alerts',
    ],
  },
  {
    name: 'Gold',
    minDeposit: '$100,000',
    popular: true,
    features: [
      'Everything in Silver',
      'Raw spreads access',
      'VIP event invitations',
      'Tax optimization guidance',
      'Priority withdrawals',
      'One-on-one coaching',
    ],
  },
  {
    name: 'VIP',
    minDeposit: '$250,000',
    popular: false,
    features: [
      'Everything in Gold',
      'Zero spread on majors',
      'Institutional liquidity',
      'Custom API access',
      'Dedicated trading desk',
      'Concierge support 24/7',
    ],
  },
  {
    name: 'SOLO',
    minDeposit: '$500,000',
    popular: false,
    features: [
      'Everything in VIP',
      'Chief portfolio manager',
      'Wealth management advisory',
      'Bespoke trading solutions',
      'Private market access',
      'Family office services',
    ],
  },
];

const testimonials = [
  {
    name: 'Alexander Braun',
    role: 'Professional Trader',
    text: "ObsidianFX execution speed is on par with institutional platforms I've used. The raw spreads on EUR/USD are consistently competitive, even during high-volatility events.",
    rating: 5,
  },
  {
    name: 'Marie Johansson',
    role: 'Portfolio Manager',
    text: 'The multi-asset access from a single account is what sold me. I manage forex, commodities, and equity CFDs seamlessly — the charting tools are genuinely professional-grade.',
    rating: 5,
  },
  {
    name: 'David Chen',
    role: 'Algorithmic Trader',
    text: "The API is well-documented and the latency is excellent. I've migrated three algo strategies to ObsidianFX and the fill quality improvement was immediately measurable.",
    rating: 5,
  },
  {
    name: 'Sofia Petrova',
    role: 'Risk Analyst',
    text: 'Impressed by the transparency — no hidden fees, clear margin requirements, and the risk tools are built into the platform rather than afterthoughts.',
    rating: 5,
  },
  {
    name: 'Thomas Muller',
    role: 'Day Trader',
    text: 'Switched from my previous broker after comparing execution reports. ObsidianFX consistently fills at the quoted price. The mobile app is also surprisingly full-featured.',
    rating: 5,
  },
  {
    name: 'Emma Richardson',
    role: 'Swing Trader',
    text: 'What I appreciate most is the education and support. My account manager actually understands markets, not just sales scripts. The weekly webinars are high quality.',
    rating: 5,
  },
  {
    name: 'Carlos Martinez',
    role: 'Commodities Specialist',
    text: "I've been trading commodities for over a decade. ObsidianFX offers some of the tightest spreads on gold and crude oil I've seen, with reliable execution around news events.",
    rating: 5,
  },
  {
    name: 'Yuki Tanaka',
    role: 'Crypto & FX Trader',
    text: 'Having crypto and forex under one roof with institutional-grade tools is a game-changer. The platform is fast, the charts are crisp, and withdrawals are processed same-day.',
    rating: 5,
  },
];

const faqs = [
  {
    q: 'What is ObsidianFX?',
    a: 'ObsidianFX is a regulated online trading platform providing access to Forex, Stocks, Indices, Commodities and Crypto CFDs. We combine institutional-grade technology with competitive pricing and dedicated client support.',
  },
  {
    q: 'How do I open a trading account?',
    a: "Click 'Open Account', complete the registration form with your personal details, verify your identity (KYC), and fund your account. The entire process takes less than 10 minutes and you can start trading immediately.",
  },
  {
    q: 'What is the minimum deposit?',
    a: 'Our Standard account requires a minimum deposit of $10,000. Higher-tier accounts — Bronze ($25,000), Silver ($50,000), Gold ($100,000), VIP ($250,000), and SOLO ($500,000) — offer progressively tighter spreads, dedicated support, and premium services.',
  },
  {
    q: 'Is my money safe with ObsidianFX?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier European banks, completely separate from company operational funds. We also offer negative balance protection and are regulated by financial authorities.',
  },
  {
    q: 'What trading platforms do you offer?',
    a: 'We offer our proprietary web-based platform, desktop applications for Windows and Mac, and fully-featured mobile apps for iOS and Android. All platforms include advanced charting, one-click trading, and real-time market data.',
  },
  {
    q: 'How can I withdraw my funds?',
    a: 'Withdrawals can be made via bank wire transfer, credit/debit card, or cryptocurrency. Requests are processed within 1-2 business days. There are no fees on standard withdrawals.',
  },
  {
    q: 'What spreads do you offer?',
    a: 'Spreads vary by account tier. Standard accounts start from competitive market spreads, while VIP and SOLO accounts offer raw spreads from 0.0 pips with institutional liquidity. Actual spreads depend on market conditions.',
  },
  {
    q: 'Do you offer educational resources?',
    a: 'Yes. We provide trading webinars, video tutorials, market analysis, economic calendar, and dedicated account managers who can guide you through your trading journey.',
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  'Forex Trading': [
    { label: 'What is Forex', href: '#' },
    { label: 'How to Trade Forex', href: '#' },
    { label: 'EUR/USD', href: '#' },
    { label: 'GBP/USD', href: '#' },
    { label: 'USD/JPY', href: '#' },
    { label: 'Forex Spreads', href: '#' },
    { label: 'Forex Strategies', href: '#' },
  ],
  'CFD Trading': [
    { label: 'What are CFDs', href: '#' },
    { label: 'Stock CFDs', href: '#' },
    { label: 'Index CFDs', href: '#' },
    { label: 'Commodity CFDs', href: '#' },
    { label: 'Gold Trading', href: '#' },
    { label: 'Oil Trading', href: '#' },
  ],
  Cryptocurrencies: [
    { label: 'Bitcoin Trading', href: '#' },
    { label: 'Ethereum Trading', href: '#' },
    { label: 'Crypto CFDs', href: '#' },
    { label: 'Crypto Spreads', href: '#' },
    { label: 'Crypto Market Hours', href: '#' },
  ],
  'Trading Info': [
    { label: 'Account Types', href: '#' },
    { label: 'Deposits & Withdrawals', href: '#' },
    { label: 'Leverage & Margin', href: '#' },
    { label: 'Trading Conditions', href: '#' },
    { label: 'Economic Calendar', href: '#' },
    { label: 'Market Analysis', href: '#' },
  ],
  About: [
    { label: 'About ObsidianFX', href: '#' },
    { label: 'Regulation & Licences', href: '#' },
    { label: 'Security of Funds', href: '#' },
    { label: 'Contact Us', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Legal Documents', href: '#' },
  ],
};

const navLinks = [
  { label: 'Markets', href: '#markets' },
  { label: 'Features', href: '#features' },
  { label: 'Accounts', href: '#accounts' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

/* ─────────────────────── ANIMATION HELPERS ─────────────────────── */

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const stagger = (i: number) => ({
  ...fadeUp,
  transition: { duration: 0.5, delay: i * 0.1 },
});

/* ─────────────────────── COMPONENT ─────────────────────── */

export default function ObsidianFXPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const prevTestimonial = () =>
    setCurrentTestimonial((p) => (p === 0 ? testimonials.length - 1 : p - 1));
  const nextTestimonial = () =>
    setCurrentTestimonial((p) => (p === testimonials.length - 1 ? 0 : p + 1));

  return (
    <div className="min-h-screen bg-[#09090B] text-[#FAFAFA]">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ── HEADER ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#09090B]/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-transparent'
        }`}
      >
        <div className="site-container flex items-center justify-between h-16 px-4 md:px-8 max-w-7xl mx-auto">
          <Link href="#" className="flex items-center gap-2">
            <span
              className="text-xl font-bold"
              style={{
                background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              ObsidianFX
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link
                key={l.label}
                href={l.href}
                className="text-sm text-[#FAFAFA] hover:text-[#F59E0B] transition-colors duration-200"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              href="#"
              className="text-sm text-[#FAFAFA] hover:text-[#F59E0B] transition-colors px-4 py-2"
            >
              Log In
            </Link>
            <Link
              href="#"
              className="text-sm font-semibold bg-[#F59E0B] hover:bg-[#D97706] text-[#09090B] px-5 py-2 rounded-lg transition-colors duration-200"
            >
              Open Account
            </Link>
          </div>

          <button className="md:hidden text-[#FAFAFA]" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#09090B]/98 backdrop-blur-md border-t border-white/[0.06] overflow-hidden"
            >
              <div className="px-4 py-4 flex flex-col gap-3">
                {navLinks.map((l) => (
                  <Link
                    key={l.label}
                    href={l.href}
                    onClick={() => setMobileOpen(false)}
                    className="text-sm text-[#FAFAFA] hover:text-[#F59E0B] py-2"
                  >
                    {l.label}
                  </Link>
                ))}
                <hr className="border-white/[0.06]" />
                <Link href="#" className="text-sm text-[#FAFAFA] py-2">
                  Log In
                </Link>
                <Link
                  href="#"
                  className="text-sm font-semibold bg-[#F59E0B] text-[#09090B] px-5 py-2.5 rounded-lg text-center"
                >
                  Open Account
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at top, rgba(245,158,11,0.15), transparent 70%)',
          }}
        />
        <div className="site-container relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.h1
            {...fadeUp}
            className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
          >
            Trade the World with{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              ObsidianFX
            </span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl text-[#A1A1AA] max-w-2xl mx-auto"
          >
            Access 180+ instruments across Forex, Stocks, Indices, Commodities and Crypto with
            institutional-grade execution and dedicated support.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              href="#"
              className="px-8 py-3.5 rounded-lg font-semibold bg-[#F59E0B] hover:bg-[#D97706] text-[#09090B] transition-colors duration-200 text-sm"
            >
              Start Trading Now
            </Link>
            <Link
              href="#"
              className="px-8 py-3.5 rounded-lg font-semibold border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors duration-200 text-sm"
            >
              Try Free Demo
            </Link>
          </motion.div>

          {/* Floating stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                {...stagger(i)}
                className="rounded-xl p-6 bg-[#18181B] border border-white/[0.06] hover:border-[#F59E0B]/20 transition-all duration-300"
              >
                <div
                  className="text-2xl md:text-3xl font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {s.value}
                </div>
                <div className="text-xs md:text-sm text-[#A1A1AA] mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="site-section py-20">
        <div className="site-container max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold">
              Why Traders Choose <span className="text-[#F59E0B]">ObsidianFX</span>
            </h2>
            <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto">
              Institutional technology meets personalized service. Everything you need to trade with
              confidence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = iconMap[f.icon];
              return (
                <motion.div
                  key={f.title}
                  {...stagger(i)}
                  className="rounded-xl p-6 bg-[#18181B] border border-white/[0.06] hover:border-[#F59E0B]/20 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#F59E0B]/10 flex items-center justify-center mb-4 group-hover:bg-[#F59E0B]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#F59E0B]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#FAFAFA] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{f.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── MARKETS ── */}
      <section id="markets" className="site-section py-20">
        <div className="site-container max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold">
              Global <span className="text-[#F59E0B]">Markets</span> at Your Fingertips
            </h2>
            <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto">
              Trade across multiple asset classes from a single account with competitive conditions.
            </p>
          </motion.div>

          <div className="flex flex-col gap-4">
            {markets.map((m, i) => (
              <motion.div
                key={m.name}
                {...stagger(i)}
                className="rounded-xl bg-[#18181B] border border-white/[0.06] hover:border-[#F59E0B]/20 transition-all duration-300 overflow-hidden"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Gold left accent */}
                  <div className="hidden md:block w-1 bg-[#F59E0B]/60 flex-shrink-0" />

                  {/* Left: market info */}
                  <div className="flex-1 p-6 md:pl-6">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-[#FAFAFA]">{m.name}</h3>
                      <span className="text-xs font-medium bg-[#F59E0B]/10 text-[#F59E0B] px-2.5 py-0.5 rounded-full">
                        {m.pairs}
                      </span>
                    </div>
                    <p className="text-sm text-[#A1A1AA] mb-4">{m.description}</p>
                    <div className="flex flex-wrap gap-4 text-xs">
                      <span className="text-[#A1A1AA]">
                        Spreads: <span className="text-[#FAFAFA] font-medium">{m.spreads}</span>
                      </span>
                      <span className="text-[#A1A1AA]">
                        Leverage: <span className="text-[#FAFAFA] font-medium">{m.leverage}</span>
                      </span>
                    </div>
                  </div>

                  {/* Right: examples */}
                  <div className="flex items-center gap-2 p-6 md:pr-8 flex-wrap md:flex-nowrap md:w-auto">
                    {m.examples.map((ex) => (
                      <span
                        key={ex}
                        className="text-xs font-medium bg-[#27272A] text-[#A1A1AA] px-3 py-1.5 rounded-lg border border-white/[0.06]"
                      >
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACCOUNTS TABLE ── */}
      <section id="accounts" className="site-section py-20">
        <div className="site-container max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold">
              Choose Your <span className="text-[#F59E0B]">Account Tier</span>
            </h2>
            <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto">
              Six tiers designed for every stage of your trading journey, from active individuals to
              family offices.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="rounded-xl bg-[#18181B] border border-white/[0.06] overflow-hidden">
                {/* Header row */}
                <div className="grid grid-cols-7 border-b border-white/[0.06]">
                  <div className="p-4" />
                  {accounts.map((a) => (
                    <div
                      key={a.name}
                      className={`p-4 text-center ${
                        a.popular ? 'bg-[#F59E0B]/10 border-x border-[#F59E0B]/30' : ''
                      }`}
                    >
                      {a.popular && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#F59E0B] block mb-1">
                          Most Popular
                        </span>
                      )}
                      <div className="text-lg font-bold text-[#FAFAFA]">{a.name}</div>
                    </div>
                  ))}
                </div>

                {/* Min deposit row */}
                <div className="grid grid-cols-7 border-b border-white/[0.06]">
                  <div className="p-4 text-sm text-[#A1A1AA] font-medium flex items-center">
                    Min. Deposit
                  </div>
                  {accounts.map((a) => (
                    <div
                      key={a.name}
                      className={`p-4 text-center ${
                        a.popular ? 'bg-[#F59E0B]/5 border-x border-[#F59E0B]/30' : ''
                      }`}
                    >
                      <span
                        className="text-base font-bold"
                        style={{
                          background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {a.minDeposit}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Feature rows */}
                {[0, 1, 2, 3, 4, 5].map((fi) => (
                  <div
                    key={fi}
                    className={`grid grid-cols-7 ${fi < 5 ? 'border-b border-white/[0.06]' : ''}`}
                  >
                    <div className="p-4 text-sm text-[#A1A1AA] flex items-center">
                      Feature {fi + 1}
                    </div>
                    {accounts.map((a) => (
                      <div
                        key={a.name}
                        className={`p-4 flex items-center justify-center gap-2 ${
                          a.popular ? 'bg-[#F59E0B]/5 border-x border-[#F59E0B]/30' : ''
                        }`}
                      >
                        <Check size={14} className="text-[#10B981] flex-shrink-0" />
                        <span className="text-xs text-[#A1A1AA] text-center leading-tight">
                          {a.features[fi]}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}

                {/* CTA row */}
                <div className="grid grid-cols-7 border-t border-white/[0.06]">
                  <div className="p-4" />
                  {accounts.map((a) => (
                    <div
                      key={a.name}
                      className={`p-4 flex items-center justify-center ${
                        a.popular ? 'bg-[#F59E0B]/5 border-x border-[#F59E0B]/30' : ''
                      }`}
                    >
                      <Link
                        href="#"
                        className={`text-xs font-semibold px-4 py-2 rounded-lg transition-colors duration-200 ${
                          a.popular
                            ? 'bg-[#F59E0B] hover:bg-[#D97706] text-[#09090B]'
                            : 'border border-white/[0.06] text-[#FAFAFA] hover:border-[#F59E0B]/40 hover:text-[#F59E0B]'
                        }`}
                      >
                        Get Started
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS CAROUSEL ── */}
      <section id="testimonials" className="site-section py-20">
        <div className="site-container max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold">
              What Our <span className="text-[#F59E0B]">Traders</span> Say
            </h2>
            <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto">
              Real feedback from real traders around the world.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="max-w-3xl mx-auto">
            <div className="rounded-xl p-8 md:p-12 bg-[#18181B] border border-white/[0.06] relative min-h-[280px] flex flex-col justify-center">
              {/* Arrows */}
              <button
                onClick={prevTestimonial}
                className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#27272A] border border-white/[0.06] hover:border-[#F59E0B]/40 flex items-center justify-center transition-colors z-10"
              >
                <ChevronLeft size={18} className="text-[#FAFAFA]" />
              </button>
              <button
                onClick={nextTestimonial}
                className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#27272A] border border-white/[0.06] hover:border-[#F59E0B]/40 flex items-center justify-center transition-colors z-10"
              >
                <ChevronRight size={18} className="text-[#FAFAFA]" />
              </button>

              {/* Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                  className="text-center px-8 md:px-12"
                >
                  {/* Stars */}
                  <div className="flex items-center justify-center gap-1 mb-6">
                    {Array.from({ length: testimonials[currentTestimonial].rating }).map((_, j) => (
                      <Star key={j} size={18} className="text-[#F59E0B] fill-[#F59E0B]" />
                    ))}
                  </div>

                  <p className="text-lg md:text-xl text-[#FAFAFA] italic leading-relaxed mb-8">
                    &ldquo;{testimonials[currentTestimonial].text}&rdquo;
                  </p>

                  <div>
                    <div className="font-semibold text-[#FAFAFA]">
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="text-sm text-[#A1A1AA]">
                      {testimonials[currentTestimonial].role}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex items-center justify-center gap-2 mt-8">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i === currentTestimonial
                        ? 'bg-[#F59E0B] w-6'
                        : 'bg-[#27272A] hover:bg-[#A1A1AA]'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="site-section py-20">
        <div className="site-container max-w-3xl mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold">
              Frequently Asked <span className="text-[#F59E0B]">Questions</span>
            </h2>
            <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto">
              Everything you need to know before getting started.
            </p>
          </motion.div>

          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...stagger(i)}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 rounded-xl bg-[#18181B] border border-white/[0.06] hover:border-[#F59E0B]/20 transition-all duration-300 text-left"
                >
                  <span className="text-sm font-medium text-[#FAFAFA] pr-4">{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-[#F59E0B] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 py-4 mt-1 rounded-xl bg-[#27272A] border border-white/[0.06]">
                        <p className="text-sm text-[#A1A1AA] leading-relaxed">{faq.a}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="site-section py-20">
        <div className="site-container max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            {...fadeUp}
            className="rounded-xl p-8 md:p-14 bg-[#18181B] border border-white/[0.06] text-center relative overflow-hidden"
          >
            {/* Gold gradient top border */}
            <div
              className="absolute top-0 left-0 right-0 h-1"
              style={{
                background: 'linear-gradient(90deg, #D97706, #F59E0B, #FBBF24, #F59E0B, #D97706)',
              }}
            />

            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start Trading?</h2>
            <p className="text-[#A1A1AA] max-w-lg mx-auto mb-8">
              Join 25,000+ traders worldwide who trust ObsidianFX for professional-grade trading
              conditions and dedicated support.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="#"
                className="px-8 py-3.5 rounded-lg font-semibold bg-[#F59E0B] hover:bg-[#D97706] text-[#09090B] transition-colors duration-200 text-sm"
              >
                Open Live Account
              </Link>
              <Link
                href="#"
                className="px-8 py-3.5 rounded-lg font-semibold border border-white/[0.06] text-[#FAFAFA] hover:border-[#F59E0B]/40 hover:text-[#F59E0B] transition-colors duration-200 text-sm"
              >
                Try Free Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#09090B] border-t border-white/[0.06] pt-16 pb-8">
        <div className="site-container max-w-7xl mx-auto px-4 md:px-8">
          {/* Link columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="text-sm font-semibold text-[#FAFAFA] mb-4">{category}</h4>
                <ul className="flex flex-col gap-2.5">
                  {links.map((l) => (
                    <li key={l.label}>
                      <Link
                        href={l.href}
                        className="text-xs text-[#A1A1AA] hover:text-[#F59E0B] transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-white/[0.06] pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Logo + legal */}
              <div className="flex flex-col items-center md:items-start gap-3">
                <span
                  className="text-lg font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  ObsidianFX
                </span>
                <p className="text-[10px] text-[#A1A1AA] max-w-md text-center md:text-left leading-relaxed">
                  Trading CFDs involves significant risk of loss and is not suitable for all
                  investors. Past performance is not indicative of future results. Please ensure you
                  fully understand the risks involved before trading.
                </p>
              </div>

              {/* Social icons */}
              <div className="flex items-center gap-4">
                {/* Twitter / X */}
                <a href="#" className="group">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#F59E0B] group-hover:opacity-80 transition-opacity"
                  >
                    <path
                      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                      fill="currentColor"
                    />
                  </svg>
                </a>
                {/* LinkedIn */}
                <a href="#" className="group">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#F59E0B] group-hover:opacity-80 transition-opacity"
                  >
                    <path
                      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                      fill="currentColor"
                    />
                  </svg>
                </a>
                {/* Telegram */}
                <a href="#" className="group">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#F59E0B] group-hover:opacity-80 transition-opacity"
                  >
                    <path
                      d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
                      fill="currentColor"
                    />
                  </svg>
                </a>
                {/* Instagram */}
                <a href="#" className="group">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#F59E0B] group-hover:opacity-80 transition-opacity"
                  >
                    <path
                      d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z"
                      fill="currentColor"
                    />
                  </svg>
                </a>
                {/* YouTube */}
                <a href="#" className="group">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-[#F59E0B] group-hover:opacity-80 transition-opacity"
                  >
                    <path
                      d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                      fill="currentColor"
                    />
                  </svg>
                </a>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-8 text-center">
              <p className="text-[10px] text-[#A1A1AA]">
                &copy; {new Date().getFullYear()} ObsidianFX. All rights reserved. ObsidianFX is a
                trading name. Trading foreign exchange on margin carries a high level of risk and
                may not be suitable for all investors.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
